/**
 * Documents domain event schemas.
 *
 * Typed payload validation for document/content-related Mycelium events.
 * Rule: fields added after initial release MUST be `.optional()`.
 *
 * @module documents.events
 */

import { z } from 'zod'
import { BackboneEventSchema } from '../types/backbone.ts'

// ── Event schemas ───────────────────────────────────────────────────────

export const ContentPostCreatedEvent = BackboneEventSchema.extend({
  domain: z.literal('documents'),
  type: z.literal('content_post_created'),
  payload: z.object({
    postId: z.string(),
    userId: z.string().optional(),
    title: z.string().optional(),
    source: z.string().optional(),
  }),
})

export const ContentPostDeletedEvent = BackboneEventSchema.extend({
  domain: z.literal('documents'),
  type: z.literal('content_post_deleted'),
  payload: z.object({
    postId: z.string(),
    userId: z.string().optional(),
    source: z.string().optional(),
  }),
})

export const WebsiteDigestDraftReadyEvent = BackboneEventSchema.extend({
  domain: z.literal('documents'),
  type: z.literal('website_digest_draft_ready'),
  payload: z.object({
    issueId: z.string(),
    channelSlug: z.string().optional(),
    slug: z.string().optional(),
    title: z.string().optional(),
    source: z.string().optional(),
  }),
})

export const WebsiteDigestPublishedEvent = BackboneEventSchema.extend({
  domain: z.literal('documents'),
  type: z.literal('website_digest_published'),
  payload: z.object({
    issueId: z.string(),
    channelSlug: z.string().optional(),
    slug: z.string().optional(),
    publishedAt: z.string().optional(),
    approvedBy: z.string().optional(),
    source: z.string().optional(),
  }),
})

export const WebsiteDigestIngestCompletedEvent = BackboneEventSchema.extend({
  domain: z.literal('documents'),
  type: z.literal('website_digest_ingest_completed'),
  payload: z.object({
    channelSlug: z.string(),
    itemCount: z.number().int().nonnegative(),
    issueId: z.string().optional(),
    source: z.string().optional(),
  }),
})

// ── All documents events ────────────────────────────────────────────────

export const DocumentsDomainEvents = [
  ContentPostCreatedEvent,
  ContentPostDeletedEvent,
  WebsiteDigestDraftReadyEvent,
  WebsiteDigestPublishedEvent,
  WebsiteDigestIngestCompletedEvent,
] as const
